import { memo, useEffect, useState } from "react";
import styled from "styled-components";
import UserAvatar from "~/layouts/components/Header/components/UserAvatar/UserAvatar";
import { PiCheck } from "react-icons/pi";
import { IoEllipsisHorizontal } from "react-icons/io5";
import { Comment_Base } from "~/common/typescript/models/Comment";
import { Popover, Tag, Tooltip } from "antd";
import { CommentAPIs } from "~/common/typescript/apis/CommentAPIs";
import { useAtom, useAtomValue } from 'jotai';
import CommentItemContextMenu from "./CommentItemContextMenu";
import CommentItemUpdateForm from "./CommentItemUpdateForm";
import { commentsAtom, newlyCreatedCommentIdAtom } from "~/components/Map/Comment/CommentStore";
import { formatRelativeTime } from "~/utils/dateUtils";
import { RichTextViewer } from "~/components/common/RichText";
import { userInformationAtom } from "~/stores/UserStore";

export enum CommentItemMode {
  View = 'view',
  Edit = 'edit',
}
interface CommentItemProps {
  comment: Comment_Base;
  mode?: CommentItemMode;
}
const CommentItem: React.FC<CommentItemProps> = (props: CommentItemProps) => {
  const { comment } = props;
  const [comments, setComments] = useAtom(commentsAtom);
  const [newlyCreatedCommentId, setNewlyCreatedCommentId] = useAtom(newlyCreatedCommentIdAtom);
  const loggedUser = useAtomValue(userInformationAtom);

  const [mode, setMode] = useState<CommentItemMode>(props.mode || CommentItemMode.View);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Sync mode with props when it changes, but only for newly created comments
  useEffect(() => {
    if (props.mode !== undefined && props.mode === CommentItemMode.Edit) {
      setMode(props.mode);
    }
  }, [props.mode, comment.id]);

  const onClickCheck = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = !comment.resolved;
    await CommentAPIs.remote_updateComment(comment.id!, {
      resolved: newValue,
    });

    setComments(
      comments.map((_comment) => {
        if (_comment.id === comment.id) {
          return {
            ...comment,
            resolved: newValue,
          };
        }
        return _comment;
      }),
    );
  };

  const switchToEditMode = () => {
    setIsPopoverOpen(false);
    setMode(CommentItemMode.Edit);
  };

  const authorUser = comment.user || loggedUser || { username: 'user', fullname: 'Không tên' };

  return (
    <UI_Wrapper data-testid={`comment-item-${comment.id}`}>
      <div style={{ width: 38, height: '100%', paddingTop: 2 }} data-testid="comment-author-avatar">
        <UserAvatar user={authorUser as any} size={30} style={{ cursor: 'default' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <UI_Fullname>
          <span>{authorUser.fullname || authorUser.username || 'Không tên'}</span>
          {comment.resolved && <Tag color="green" style={{ marginLeft: 6 }}>Xong</Tag>}
        </UI_Fullname>
        <UI_CreatedAt>{formatRelativeTime(comment.createdAt)}</UI_CreatedAt>
        {mode === CommentItemMode.View ? (
          <>
            <UI_Title style={{ textDecoration: comment.is_deleted ? 'line-through' : 'none' }}>
              {comment.title}
            </UI_Title>
            <UI_Content>
              <RichTextViewer content={comment.description} strikethrough={comment.is_deleted} />
            </UI_Content>
          </>
        ) : (
          <CommentItemUpdateForm
            comment={comment}
            callbackSaved={() => {
              if (newlyCreatedCommentId === comment.id) {
                setNewlyCreatedCommentId(null);
              }
              setMode(CommentItemMode.View);
            }}
            callbackCanceled={() => {
              if (newlyCreatedCommentId === comment.id) {
                setNewlyCreatedCommentId(null);
              }
              setMode(CommentItemMode.View);
            }}
          />
        )}
      </div>
      {mode === CommentItemMode.View && (
        <UI_Right className="right">
          <Tooltip title="Đánh dấu là hoàn thành">
            <UI_HeaderRight_item onClick={onClickCheck} data-testid="comment-resolve-btn">
              <PiCheck color={comment.resolved ? 'green' : 'black'} />
            </UI_HeaderRight_item>
          </Tooltip>
          <Popover
            open={isPopoverOpen}
            onOpenChange={setIsPopoverOpen}
            trigger="click"
            placement="bottomRight"
            title={null}
            arrow={false}
            content={
              <CommentItemContextMenu
                comment={comment}
                onClickEdit={switchToEditMode}
                onCloseContextMenu={() => setIsPopoverOpen(false)}
              />
            }
          >
            <UI_HeaderRight_item
              data-testid="comment-more-btn"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <IoEllipsisHorizontal />
            </UI_HeaderRight_item>
          </Popover>
        </UI_Right>
      )}
    </UI_Wrapper>
  );
};

export default CommentItem;

const UI_Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding: 8px 8px;
  position: relative;

  &:hover .right {
    display: flex;
  }
`;

const UI_Fullname = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
`;

const UI_CreatedAt = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  margin-bottom: 4px;
`;
const UI_Title = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 3px;
`;

const UI_Content = styled.div`
  font-size: 12.5px;
  color: #334155;
  margin-bottom: 4px;
`;

const UI_Right = styled.div`
  display: none;
  position: absolute;
  right: 8px;
  top: 8px;
  flex-direction: row;
  gap: 4px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
`;
const UI_HeaderRight_item = styled.div`
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;

  &:hover {
    background-color: #f0f0f0;
  }
`;
